import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import { FadeIn, ChapterHeading, EditorialMarquee } from "@/components/motion";
import { IMAGES } from "@/config/site";

const STEPS = [
  {
    num: "01",
    title: "Reach out",
    text: "Tell us about your situation through the Request Care form — or ask your hospital team to send a referral. Either way works.",
  },
  {
    num: "02",
    title: "A conversation",
    text: "We talk with you and your family about what the transition home should look like — your routines, your worries, your hopes.",
  },
  {
    num: "03",
    title: "A plan around you",
    text: "Together we shape a plan for the days after discharge, so everyone knows what happens next before it needs to happen.",
  },
];

const EXPECTATIONS = [
  {
    num: "01",
    title: "Listening before planning",
    text: "The first thing we offer is attention. Understanding the person comes before organizing the care.",
  },
  {
    num: "02",
    title: "One clear thread",
    text: "Hospital instructions, follow-up appointments, questions along the way — kept connected and explained in plain language.",
  },
  {
    num: "03",
    title: "Family included",
    text: "The people who love you are part of the plan. We make sure they feel informed, prepared, and supported too.",
  },
];

export default function Patients() {
  return (
    <div data-testid="patients-page">
      <PageHeader
        eyebrow="For Patients & Families"
        lines={["Bridging the Gap,", "Supporting What Matters."]}
        intro="The move from hospital to home can feel like stepping into the unknown. Homevia exists to make that step feel supported, understood, and unhurried."
        testid="patients-heading"
      />

      <section className="px-6 pb-24 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <FadeIn>
            <h2 className="font-serif text-4xl tracking-tight text-ink lg:text-5xl">How care begins</h2>
          </FadeIn>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeIn key={s.num} delay={i * 0.12}>
                <div className="h-full rounded-3xl border border-olive/10 bg-white p-8 transition-all duration-500 hover:scale-[1.02] hover:border-teal/50">
                  <span className="font-display italic text-lg text-teal">({s.num})</span>
                  <h3 className="mt-6 font-serif text-2xl text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-olive/70">{s.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee items={["You are not alone", "One step at a time", "Home is part of healing"]} testid="patients-marquee" />

      <section className="px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeIn>
              <div className="relative">
                <div className="absolute -inset-4 -rotate-1 rounded-[2.5rem] bg-sage/20" aria-hidden />
                <div className="relative overflow-hidden rounded-[2rem] border border-olive/10">
                  <img
                    src={IMAGES.family}
                    alt="Grandparents laughing while hugging their grandchildren outdoors in golden light"
                    data-testid="patients-family-image"
                    className="h-[380px] w-full object-cover lg:h-[500px]"
                  />
                </div>
              </div>
            </FadeIn>
          </div>
          <div className="lg:col-span-7">
            <FadeIn>
              <h2 className="font-serif text-4xl tracking-tight text-ink lg:text-5xl">What to expect</h2>
            </FadeIn>
            <div className="mt-10 space-y-10">
              {EXPECTATIONS.map((e) => (
                <div key={e.num} className="border-t border-olive/10 pt-8">
                  <ChapterHeading number={e.num} title={e.title} />
                  <FadeIn delay={0.1}>
                    <p className="mt-4 max-w-xl text-olive/75">{e.text}</p>
                  </FadeIn>
                </div>
              ))}
            </div>
            <FadeIn delay={0.3} className="mt-12">
              <Link
                to="/request-care"
                data-testid="patients-request-care-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-deep px-7 py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-olive hover:shadow-xl"
              >
                Request Care <ArrowRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
